import type { MockServer } from '@@/api/mock-server/server.js'
import {
  randNumber,
  randEmail,
  randAlphaNumeric,
  randFloat,
  rand,
  randFullName,
  randPhoneNumber,
} from '@ngneat/falso'

export function routes(server: MockServer): void {
  server.get('/invoice_runs/invoice_run_info', (_, request) => {
    const raw = (request.queryParams?.invoice_ids as string) || ''
    const invoiceIdsArray = raw
      .split(',')
      .map((id) => id.trim())
      .filter(Boolean)

    if (invoiceIdsArray.length === 0) {
      return {
        data: [],
      }
    }

    const invoiceIdPrefix = 'send'
    const prefixInvoiceId = (count: string) => invoiceIdPrefix + count

    const mockInvoiceRunIds = [
      // Valid
      { id: prefixInvoiceId('1') },
      { id: prefixInvoiceId('2') },

      // Draft
      { id: prefixInvoiceId('3'), isDraft: true },
      { id: prefixInvoiceId('4'), isDraft: true },

      // Missing email or no email consent
      { id: prefixInvoiceId('5'), missingEmail: true },
      { id: prefixInvoiceId('6'), noEmailConsent: true },

      // No client ownership
      { id: prefixInvoiceId('7'), noClientOwnership: true },
      { id: prefixInvoiceId('8'), noClientOwnership: true },
    ]
    const mockInvoiceData = ({
      isDraft = false,
      missingEmail = false,
      noEmailConsent = false,
      noClientOwnership = false,
    }: {
      isDraft?: boolean
      missingEmail?: boolean
      noEmailConsent?: boolean
      noClientOwnership?: boolean
    }) => {
      return {
        attributes: {
          invoice_number: randAlphaNumeric({ length: 10 }).join(''),
          invoice_total: randFloat({
            min: 10,
            max: 10000,
            fractionDigits: 2,
          }),
          currency: rand(['EUR', 'USD', 'GBP', 'SEK', 'NOK', 'DKK']),
          is_draft: isDraft,
          validation_errors: [
            ...(isDraft
              ? [{ code: 'draft_invoice', message: 'Draft invoice' }]
              : []),
            ...(missingEmail
              ? [{ code: 'missing_email', message: 'Missing email' }]
              : []),
            ...(noEmailConsent
              ? [{ code: 'no_email_consent', message: 'No email consent' }]
              : []),
            ...(noClientOwnership
              ? [
                  {
                    code: 'no_client_ownership',
                    message: 'No client ownership',
                  },
                ]
              : []),
          ],
          client: noClientOwnership
            ? {}
            : {
                id: randNumber({ min: 1, max: 9999 }),
                name: randFullName(),
                email: missingEmail ? undefined : randEmail(),
                phone: randPhoneNumber(),
                has_email_consent: !noEmailConsent,
              },
        },
      }
    }

    const requestedIds = new Set(invoiceIdsArray)
    const matchingInvoices = mockInvoiceRunIds.filter((item) =>
      requestedIds.has(item.id),
    )

    return {
      data: matchingInvoices.map((item) => {
        return {
          id: item.id,
          type: 'InvoiceRunInfo',
          ...mockInvoiceData({
            isDraft: item.isDraft,
            missingEmail: item.missingEmail,
            noEmailConsent: item.noEmailConsent,
            noClientOwnership: item.noClientOwnership,
          }),
        }
      }),
    }
  })

  server.get('/invoice_runs/email_sender_choices', () => {
    return {
      data: {
        type: 'EmailSenderChoices',
        id: randNumber({ min: 1, max: 9999 }).toString(),
        attributes: {
          choices: [
            {
              id: 'org_1',
              label: 'Clinic Name (clinic@example.com)',
              email: 'clinic@example.com',
              sender_type: 'organization',
            },
            {
              id: 'dept_1',
              label: 'Main Department (department@example.com)',
              email: 'department@example.com',
              sender_type: 'department',
            },
          ],
          attachment_defaults: {
            consultation_history_attachment: true,
            discharge_instructions: false,
            enable_feedback: false,
            invoice_attachment: false,
            payment_receipts_attachment: false,
          },
          default_sender_id: 'dept_1',
        },
      },
    }
  })

  server.get('/invoice_runs/can_finalize', (_, request) => {
    const raw = (request.queryParams?.invoice_ids as string) || ''
    const invoiceIdsArray = raw
      .split(',')
      .map((id) => id.trim())
      .filter(Boolean)

    return invoiceIdsArray.map((id) => ({
      id,
      type: 'CanFinalizeResult',
      attributes: {
        id: Number.parseInt(id, 10) || 0,
        can_be_finalized: true,
      },
    }))
  })

  server.post('/invoice_runs/finalize', () => {
    return {
      data: {
        type: 'InvoiceFinalization',
        id: randNumber({ min: 1, max: 9999 }).toString(),
        attributes: {
          task_id: randAlphaNumeric({ length: 10 }).join(''),
        },
      },
    }
  })

  server.get('/invoice_runs/finalize/status', (_, request) => {
    const taskId =
      (request.queryParams?.task_id as string) ||
      randAlphaNumeric({ length: 10 }).join('')

    return {
      data: {
        type: 'InvoiceFinalizationStatus',
        id: randNumber({ min: 1, max: 9999 }).toString(),
        attributes: {
          status: 'SUCCESS',
          task_id: taskId,
        },
      },
    }
  })

  server.post('/invoice_runs/send', () => {
    return {
      data: {
        type: 'InvoiceSend',
        id: randNumber({ min: 1, max: 9999 }).toString(),
      },
    }
  })
}
